import {
    useCallback,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Link } from 'react-router';
import {
    ExternalLinkIcon,
    ForwardIcon,
    Maximize2Icon,
    MinusIcon,
    PencilIcon,
    UserRoundIcon,
    XIcon,
} from 'lucide-react';
import { useActivitySensor } from '@archipro-website/react-hooks';
import { cn } from '@rocco/utils/cn';
import type { MeUser } from '~/server/types';
import type { Conversation, TextOrFileMessage } from '../../types';
import { ConversationProvider, useConversation } from '../../context';
import { useChatBoxes } from '../../hooks/use-chat-boxes';
import { useChatGlobals } from '../../hooks/use-chat-globals';
import { useConversations } from '../../hooks/use-conversations';
import { useRealTimeChat } from '../../hooks/use-real-time-chat';
import {
    CHAT_BOX_CLOSED_WIDTH_REM,
    CHAT_BOX_OPEN_WIDTH_PX,
    DRAWER_HEADER_HEIGHT_REM,
    getChatSubtitle,
    getChatTitle,
    getEnquiryConversationProfessional,
} from '../../utils';
import { getInboxURL } from '../../utils/url';
import { ChatDropOverlay } from '../chat-details/ChatDropOverlay';
import { ChatProfessionalBanner } from '../chat-details/ChatProfessionalBanner';
import { ChatBranchEnquiryBanner } from '../chat-details/ChatBranchEnquiryBanner';
import { ChatInput, type ChatInputHandle } from '../chat-input/ChatInput';
import { ChatMessages } from '../messages/ChatMessages';
import { ChatNewMessage } from '../new-message/ChatNewMessage';
import { ChatSettings } from '../chat-settings/ChatSettings';
import { NotificationBadge } from '../NotificationBadge';
import { ChatBoxAvatar } from './ChatBoxAvatar';
import { ChatBoxMoreOptions } from './ChatBoxMoreOptions';

/**
 * A chat box containing a single chat conversation.
 * - The chat box text size should remain the same regardless of window size,
 *   unlike the rest of the website, so it should use `px` instead of `rem`.
 */
export function ChatBox({
    className,
    user,
    conversationID,
    professionalID,
    conversation: partialConversation,
}: {
    className?: string;
    /**
     * The current logged-in user.
     */
    user: MeUser;
    /**
     * The ID of the conversation. Set to `0` to create a new conversation.
     */
    conversationID: number;
    /**
     * The professional ID if this conversation was opened from the company inbox
     * and is being viewed from the perspective of the professional. If `null`,
     * the personal inbox context will be used.
     */
    professionalID: number | null;
    /**
     * The conversation object from the conversations list query, which contains
     * only the latest 7 messages. The paginated messages will be fetched when
     * this component is mounted. This prop is used to provide a preview of the
     * conversation before the messages are fully loaded. This may be `undefined`
     * if the conversation was not in the paginated conversations list query.
     */
    conversation?: Conversation;
}) {
    const { closeChatBox } = useChatBoxes();
    const { removeConversation } = useConversations();

    useRealTimeChat({
        onMessage: (message) => {
            if (
                message.conversation_id === conversationID &&
                message.type === 'chat/removed' &&
                ((!professionalID && message.member_id) ||
                    (professionalID &&
                        message.professional_id === professionalID))
            ) {
                closeChatBox(conversationID, professionalID);
                removeConversation(conversationID, professionalID);
            }
        },
    });

    return (
        <ConversationProvider
            conversationID={conversationID}
            professionalID={professionalID}
            conversation={partialConversation}
        >
            <ChatBoxContent className={className} user={user} />
        </ConversationProvider>
    );
}

function ChatBoxContent({
    className,
    user,
}: {
    className?: string;
    user: MeUser;
}) {
    const chatBoxId = useId();
    const chatInputRef = useRef<ChatInputHandle>(null);
    const dragCounterRef = useRef(0);
    const [isDragOver, setIsDragOver] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [disableAutoRead, setDisableAutoRead] = useState(false);
    const [isFileAttachmentSent, setIsFileAttachmentSent] = useState(false);
    const [replyingTo, setReplyingTo] = useState<TextOrFileMessage | null>(
        null,
    );
    const [scrollDownCounter, setScrollDownCounter] = useState(0);
    const pageActive = useActivitySensor();
    const {
        pendingMessage,
        forwardedMessage,
        setPendingMessage,
        setForwardedMessage,
    } = useChatGlobals();
    const {
        isChatBoxOpen,
        openChatBox,
        minimizeChatBox,
        closeChatBox,
        convertNewMessageToConversation,
    } = useChatBoxes();
    const { removeConversation } = useConversations();
    const {
        conversationID,
        professionalID,
        conversation,
        messages,
        isParticipant,
        isLoading,
        isSubmitting,
        isMarkAsReadBusy,
        createMessage,
        markAsRead,
        markAsUnread,
    } = useConversation();
    const isNewConversation = conversationID <= 0;
    const isOpen = isChatBoxOpen(conversationID, professionalID);
    const unreadCount = conversation?.UnreadMessageCount ?? 0;
    const professional = useMemo(() => {
        if (professionalID) {
            return (
                user.Professionals.find(
                    (professional) => professional.ID === professionalID,
                ) ?? null
            );
        }
        return null;
    }, [professionalID, user]);
    const title = useMemo(() => {
        if (isNewConversation) {
            return forwardedMessage ? 'Forward Message' : 'New Message';
        }
        return getChatTitle(conversation, user.ID, professionalID);
    }, [
        isNewConversation,
        forwardedMessage,
        conversation,
        user.ID,
        professionalID,
    ]);
    const subtitle = useMemo(() => {
        return getChatSubtitle(conversation, user.ID, professionalID);
    }, [conversation, user.ID, professionalID]);
    const enquiryProfessional =
        conversation?.__typename === 'EnquiryConversation'
            ? getEnquiryConversationProfessional(conversation)
            : null;
    const isDirectConversation =
        !isNewConversation && conversation?.__typename === 'DirectConversation';
    const isFileUploadDisabled =
        !chatInputRef.current || chatInputRef.current.isFileUploadDisabled;

    const sendMessage = useCallback(
        (message: string, attachments: File[], replyTo?: TextOrFileMessage) => {
            if (attachments?.length) {
                setIsFileAttachmentSent(true);
            } else {
                setIsFileAttachmentSent(false);
            }
            createMessage({ message, attachments, replyTo });
            // Clear reply state after sending.
            setReplyingTo(null);
        },
        [createMessage],
    );

    // Mark the conversation as read if the chat box is open.
    useEffect(() => {
        if (
            isParticipant &&
            !isLoading &&
            conversation &&
            conversation.UnreadMessageCount > 0 &&
            isOpen &&
            pageActive &&
            !disableAutoRead &&
            !isMarkAsReadBusy
        ) {
            markAsRead();
        }
    }, [
        isParticipant,
        isLoading,
        conversation,
        isOpen,
        pageActive,
        disableAutoRead,
        isMarkAsReadBusy,
        markAsRead,
    ]);

    // Re-enable auto-read whenever the chat open state changes.
    useEffect(() => {
        setDisableAutoRead(false);
    }, [conversationID, professionalID, isOpen]);

    // Automatically send the pending message when this chat box was converted
    // from the new message chat box.
    useEffect(() => {
        if (
            pendingMessage?.conversationID === conversationID &&
            pendingMessage.professionalID === professionalID
        ) {
            sendMessage(
                pendingMessage.message,
                pendingMessage.attachments,
                pendingMessage.replyTo,
            );
            setPendingMessage(null);
        }
    }, [
        conversationID,
        professionalID,
        pendingMessage,
        sendMessage,
        setPendingMessage,
    ]);

    return (
        <aside
            id={chatBoxId}
            className={cn(
                'flex h-[640px] flex-col rounded-t-[16px] bg-white shadow-chat transition-[height,width] duration-200 ease-linear will-change-[height,width]',
                className,
            )}
            style={{
                width: isOpen
                    ? `${CHAT_BOX_OPEN_WIDTH_PX}px`
                    : `${CHAT_BOX_CLOSED_WIDTH_REM}rem`,
                height: isOpen ? undefined : `${DRAWER_HEADER_HEIGHT_REM}rem`,
            }}
            data-state={isOpen ? 'open' : 'closed'}
            aria-label={title}
            onDrop={(e) => {
                // Prevent drag and drop opening file in browser.
                if (e.dataTransfer.files.length) {
                    e.preventDefault();
                    e.stopPropagation();
                }
            }}
            onDragOver={(e) => {
                if (e.dataTransfer.types.includes('Files')) {
                    e.preventDefault();
                    e.stopPropagation();
                }
            }}
        >
            {/* Header */}
            <header
                className={cn(
                    'relative flex items-center rounded-t-[16px]',
                    isOpen
                        ? 'gap-[12px] p-[12px]'
                        : 'gap-2 px-2 hover:bg-black/10',
                )}
                style={{
                    height: isOpen
                        ? undefined
                        : `${DRAWER_HEADER_HEIGHT_REM}rem`,
                }}
                role={isOpen ? undefined : 'button'}
                aria-label={isOpen ? undefined : 'Open chat'}
                aria-expanded={isOpen}
                aria-controls={chatBoxId}
                onClick={
                    isOpen
                        ? undefined
                        : () => openChatBox(conversationID, professionalID)
                }
            >
                {isNewConversation ? (
                    <div
                        className={cn(
                            'flex items-center justify-center rounded-full bg-white',
                            isOpen ? 'size-[40px]' : 'size-9',
                        )}
                    >
                        {forwardedMessage ? (
                            <ForwardIcon
                                className={cn(
                                    'text-gray-600',
                                    isOpen ? 'size-[20px]' : 'size-4.5',
                                )}
                            />
                        ) : (
                            <PencilIcon
                                className={cn(
                                    'text-gray-600',
                                    isOpen ? 'size-[20px]' : 'size-4.5',
                                )}
                                strokeWidth="1.5px"
                            />
                        )}
                    </div>
                ) : (
                    <ChatBoxAvatar user={user} isOpen={isOpen} />
                )}
                <div
                    className={cn(
                        'flex min-w-0 flex-1 items-center',
                        isOpen ? 'gap-[8px]' : 'gap-2',
                    )}
                >
                    <div className="min-w-0 flex-1 space-y-[2px] leading-tight text-black">
                        {!!enquiryProfessional &&
                        title === enquiryProfessional.Title &&
                        isOpen ? (
                            <Link
                                to={enquiryProfessional.Link}
                                className="block truncate text-[14px] font-medium hover:underline"
                                title={title}
                            >
                                {title}
                            </Link>
                        ) : (
                            <div
                                className={cn(
                                    'truncate font-medium',
                                    isOpen ? 'text-[14px]' : 'text-4',
                                )}
                                title={title}
                            >
                                {title}
                            </div>
                        )}
                        {!isNewConversation &&
                            (isDirectConversation && conversation.BuildHub ? (
                                <Link
                                    to={conversation.BuildHub.Link}
                                    className={cn(
                                        'flex w-fit max-w-full items-center gap-[4px] text-black/70 hover:text-black hover:underline',
                                        !isOpen && 'hidden',
                                    )}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <span className="truncate text-[12px]">
                                        {subtitle}
                                    </span>
                                    <ExternalLinkIcon className="size-[12px] shrink-0" />
                                </Link>
                            ) : (
                                <div
                                    className={cn(
                                        'truncate text-[12px] text-black/70',
                                        !isOpen && 'hidden',
                                    )}
                                >
                                    {subtitle}
                                </div>
                            ))}
                    </div>
                    <NotificationBadge
                        className={cn(
                            isOpen
                                ? 'h-[18px] min-w-[18px] p-[4px] text-[12px]'
                                : 'h-4.5 min-w-4.5 p-1 text-[0.75rem]',
                        )}
                        count={unreadCount}
                    />
                    {isDirectConversation && (
                        <button
                            className={cn(
                                'flex items-center gap-[6px] rounded-[8px] border border-black/50 px-[8px] py-[4px] text-[13px] text-black/70 outline-0! hover:bg-black/10 focus-visible:bg-black/10',
                                !isOpen && 'hidden',
                            )}
                            aria-label="Chat settings"
                            onClick={() => setShowSettings((state) => !state)}
                        >
                            <UserRoundIcon className="size-[16px]" />
                            {conversation.Participants.length}
                        </button>
                    )}
                    <div className="flex items-center gap-[2px]">
                        <ChatBoxMoreOptions
                            className={cn(!isOpen && 'hidden')}
                            onToggleRead={() => {
                                if (unreadCount > 0) {
                                    markAsRead();
                                    setDisableAutoRead(false);
                                } else {
                                    markAsUnread();
                                    setDisableAutoRead(true);
                                }
                            }}
                        />
                        <button
                            className={cn(
                                'grid size-[36px] place-items-center rounded-full outline-black/20! hover:bg-black/10 focus-visible:outline!',
                                !isOpen && 'hidden',
                            )}
                            aria-label="Minimize chat box"
                            onClick={() =>
                                minimizeChatBox(conversationID, professionalID)
                            }
                        >
                            <MinusIcon className="size-[16px]" />
                        </button>
                        <Link
                            to={getInboxURL(
                                professional?.URLSegment ?? null,
                                conversationID,
                            )}
                            className={cn(
                                'grid size-[36px] place-items-center rounded-full outline-black/20 hover:bg-black/10 focus-visible:outline',
                                !isOpen && 'hidden',
                            )}
                            aria-label="Open chat in full-page inbox"
                        >
                            <Maximize2Icon className="size-[16px]" />
                        </Link>
                        <button
                            className={cn(
                                'grid size-[36px] place-items-center rounded-full outline-black/20! hover:bg-black/10 focus-visible:outline!',
                                !isOpen && 'size-10',
                            )}
                            aria-label="Close chat box"
                            onClick={(e) => {
                                e.stopPropagation();
                                closeChatBox(conversationID, professionalID);
                            }}
                        >
                            <XIcon
                                className={cn(
                                    'size-[16px]',
                                    !isOpen && 'size-5',
                                )}
                            />
                        </button>
                    </div>
                </div>
            </header>
            <section
                className={cn(
                    'relative flex min-h-0 flex-1 flex-col gap-[8px] p-[8px] pb-8',
                    !isOpen && 'hidden',
                )}
                onDragEnter={(e) => {
                    dragCounterRef.current++;
                    if (isFileUploadDisabled) return;
                    if (e.dataTransfer.types.includes('Files')) {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsDragOver(true);
                    }
                }}
                onDragLeave={(e) => {
                    dragCounterRef.current--;
                    if (isFileUploadDisabled || !isDragOver) return;
                    e.preventDefault();
                    e.stopPropagation();
                    if (dragCounterRef.current <= 0) {
                        setIsDragOver(false);
                    }
                }}
                onDrop={(e) => {
                    dragCounterRef.current = 0;
                    if (isFileUploadDisabled || !isDragOver) return;
                    // Drag and drop files to upload.
                    if (e.dataTransfer.files.length) {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsDragOver(false);
                        chatInputRef.current.addFiles(e.dataTransfer.files);
                    }
                }}
            >
                {isDragOver && !isFileUploadDisabled && <ChatDropOverlay />}
                {isNewConversation ? (
                    <ChatNewMessage
                        className="min-h-0 flex-1"
                        chatInputRef={chatInputRef}
                        isOpen={isOpen}
                        onOpenConversation={openChatBox}
                        onSwitchToConversation={convertNewMessageToConversation}
                    />
                ) : showSettings && isDirectConversation ? (
                    <ChatSettings
                        className="min-h-0 flex-1"
                        conversation={conversation}
                        onClose={() => setShowSettings(false)}
                        onLeaveSuccess={() => {
                            closeChatBox(conversationID, professionalID);
                            removeConversation(conversationID, professionalID);
                        }}
                        onGroupChatCreated={(groupChatID) => {
                            openChatBox(groupChatID, professionalID);
                        }}
                    />
                ) : (
                    <>
                        <ChatProfessionalBanner />
                        <ChatBranchEnquiryBanner />
                        <ChatMessages
                            className="min-h-0 flex-1"
                            messages={messages}
                            scrollDownCounter={scrollDownCounter}
                            isOpen={isOpen}
                            onReplyMessage={(message) => {
                                setReplyingTo(message);
                                setScrollDownCounter((state) => state + 1);
                            }}
                            onForwardMessage={(message) => {
                                setForwardedMessage(message);
                                openChatBox(0, null);
                            }}
                            onOpenConversation={openChatBox}
                        />
                        <ChatInput
                            ref={chatInputRef}
                            isOpen={isOpen}
                            // Only show the submitting state when a message is sent
                            // WITH file attachments. This is prevent sending too quickly
                            // causing messages being sent in the wrong order.
                            isSubmitting={isSubmitting && isFileAttachmentSent}
                            replyingTo={replyingTo}
                            replyMode="reply"
                            onCancelReply={() => {
                                setReplyingTo(null);
                                setScrollDownCounter((state) => state + 1);
                            }}
                            onSend={({ message, attachments, replyTo }) => {
                                sendMessage(message, attachments, replyTo);
                            }}
                            onAttachmentsChanged={() => {
                                setScrollDownCounter((state) => state + 1);
                            }}
                        />
                    </>
                )}
            </section>
        </aside>
    );
}
